import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import PremiumSection from "../../home/components/PremiumSection";
import { normalizeLanguage } from "../../../utils/language";
import type { LegalDocument } from "../content/termsContent";
import type { PrivacyDocument } from "../content/privacyContent";

type LegalDocContent = LegalDocument | PrivacyDocument;

type LegalDocumentPageProps = {
  document: Record<"tr" | "en", LegalDocContent>;
  type: "terms" | "privacy";
};

function LegalDocumentPage({ document, type }: LegalDocumentPageProps) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLanguage(i18n.language);
  const doc = document[lang] as LegalDocContent;

  if (!doc) return null;

  const isPrivacy = type === "privacy";

  return (
    <div className="min-h-screen bg-gradient-to-b from-blog to-customPurple-premium">
      <div className="legal-document w-[100%] md:w-[90%] max-w-4xl mx-auto pt-44 pb-20 px-6 md:px-10">
        <div className="legal-content text-white">
          <nav className="mb-8 text-sm text-white/80">
            <Link to="/" className="hover:text-white transition-colors">
              {t("nav.home")}
            </Link>
            <span className="mx-2">/</span>
            <Link
              to={isPrivacy ? "/gizlilik-sozlesmesi" : "/hukum-ve-kosullar"}
              className="hover:text-white transition-colors"
            >
              {isPrivacy
                ? t("legal.privacyPolicy")
                : t("legal.termsAndConditions")}
            </Link>
          </nav>

          <h1 className="text-3xl md:text-4xl font-bold mb-2">{doc.title}</h1>
          {"subtitle" in doc && (
            <p className="text-lg text-white/90 mb-6">{doc.subtitle}</p>
          )}
          {"lastUpdated" in doc && doc.lastUpdated && (
            <p className="text-sm text-white/70 mb-10">{doc.lastUpdated}</p>
          )}

          {isPrivacy && "intro" in doc && doc.intro && (
            <div className="mb-10 space-y-4">
              {doc.intro.map((paragraph, i) => (
                <p key={i} className="text-white/90 leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
          )}

          <div className="space-y-10">
            {doc.sections.map((section, idx) => (
              <section key={idx} className="legal-section">
                <h2 className="text-xl md:text-2xl font-semibold mb-4 text-white">
                  {section.heading}
                </h2>
                <div className="space-y-3">
                  {section.paragraphs.map((para, pIdx) => (
                    <p
                      key={pIdx}
                      className="text-white/90 leading-relaxed text-justify"
                    >
                      {para}
                    </p>
                  ))}
                </div>
              </section>
            ))}
          </div>

          {isPrivacy && "contact" in doc && doc.contact && (
            <section className="mt-14 p-6 rounded-xl bg-white/5 border border-white/10">
              <h2 className="text-xl font-semibold mb-4 text-white">
                {t("legal.contactInfo")}
              </h2>
              <div className="space-y-2 text-white/90">
                <p>
                  <span className="font-medium text-white">
                    {t("legal.company")}:
                  </span>{" "}
                  {doc.contact.company}
                </p>
                <p>
                  <span className="font-medium text-white">
                    {t("legal.email")}:
                  </span>{" "}
                  <a
                    href={`mailto:${doc.contact.email}`}
                    className="text-customPurple-light hover:underline"
                  >
                    {doc.contact.email}
                  </a>
                </p>
                <p>
                  <span className="font-medium text-white">
                    {t("legal.address")}:
                  </span>{" "}
                  {doc.contact.address}
                </p>
              </div>
            </section>
          )}
        </div>
      </div>
      <PremiumSection className="bg-gradient-to-b from-transparent to-blog mt-20" />
    </div>
  );
}

export default LegalDocumentPage;
